/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type EventCallBacks } from '@adp-studio/autosave';

import { usePageRuntimeStore } from '@/store/page-runtime';
import { type BizKey, type ScopeStateType } from '@/save-manager/types';

const update = ({
  scope,
  status,
  revision,
  error,
}: {
  scope: string;
  status: 'clean' | 'pending' | 'saving' | 'failed';
  revision: number;
  error?: Error;
}) =>
  usePageRuntimeStore.getState().updateSavingScope({
    scopeKey: scope,
    status,
    revision,
    error,
  });

export const autosaveEventCallBacks: EventCallBacks<ScopeStateType, BizKey> = {
  onDirty: ({ scope, revision }) =>
    update({ scope, status: 'pending', revision }),
  onSaving: ({ scope, revision }) =>
    update({ scope, status: 'saving', revision }),
  onSuccess: ({ scope, revision }) =>
    update({ scope, status: 'clean', revision }),
  onError: ({ scope, revision, error }) =>
    update({ scope, status: 'failed', revision, error }),
};
